import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Home, ArrowLeft, Search, BookOpen } from 'lucide-react';
import { Button } from '../components/shared/Button';

export const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const suggestions = [
    { label: t('header.dashboard'), path: '/dashboard', icon: Home },
    { label: t('header.startExam'), path: '/exam-selection', icon: BookOpen },
    { label: t('header.history'), path: '/exam-history', icon: Search },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      {/* Simple Header */}
      <header className="py-4 px-6 border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center font-bold text-lg">
              NT2
            </div>
            <span className="text-xl font-bold text-primary">{t('common.appName')}</span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-16">
        {/* Animated 404 */}
        <div className="relative mb-8">
          {/* Background circles */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-64 h-64 rounded-full bg-error/5 animate-pulse" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-48 h-48 rounded-full bg-error/10 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
          
          {/* Icon */}
          <div className="relative w-32 h-32 rounded-full bg-error/20 flex items-center justify-center">
            <AlertTriangle className="w-16 h-16 text-error" />
          </div>
        </div>

        {/* Error Message */}
        <h1 className="text-8xl font-bold text-gray-900 mb-4">404</h1>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('notFound.title')}</h2>
        <p className="text-gray-600 mb-8 max-w-md text-center">
          {t('notFound.subtitle')}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-12">
          <Button
            variant="tertiary"
            onClick={() => navigate(-1)}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            {t('notFound.goBack')}
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate('/')}
            icon={<Home className="w-4 h-4" />}
          >
            {t('notFound.home')}
          </Button>
        </div>

        {/* Suggestions */}
        <div className="w-full max-w-md">
          <p className="text-sm text-gray-500 text-center mb-4">
            {t('common.or')}:
          </p>
          <div className="grid grid-cols-3 gap-3">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.path}
                onClick={() => navigate(suggestion.path)}
                className="flex flex-col items-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-2 group-hover:bg-primary/20 transition-colors">
                  <suggestion.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-gray-700">{suggestion.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Fun illustration */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
            <span>🔍</span>
            <span>{t('landing.faq.notFound')}</span>
            <button 
              onClick={() => navigate('/contact')}
              className="text-primary font-medium hover:underline"
            >
              {t('footer.contact')}
            </button>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="py-6 px-6 border-t border-gray-100 text-center">
        <p className="text-sm text-gray-500">
          © {new Date().getFullYear()} {t('common.appName')}. {t('footer.allRightsReserved')}.
        </p>
      </footer>
    </div>
  );
};

export default NotFoundPage;
